import React, {useState} from "react";
import * as yup from "yup";
import {Modal} from "reactstrap";
import {ErrorMessage, Field, Form, Formik} from "formik";
import {components} from "react-select";

import {errorMessage, invalidEmailFormat, requiredError} from "../../constants/errorMessages";
import mailWithArrow from "assets/images/ic-mail-with-arrow.svg";
import {useChosenPatientContext} from "pages/Dashboard/tableContext";
import toastr from 'components/toastr';
import PatientsManagementService from "services/patientsManagement";
import Label from "components/Label/labelWithError";
import Select from "components/Select";
import strictValidateEmail from "../validateFunctions/strictEmailValidate";

const { Option: OptionComponent } = components;

const TemplatesToSendGPOptions = [
    {label: 'Private GP Letter', value: 0},
    {label: 'Private GP Letter - action required', value: 1},
    {label: 'Private GP Letter – urgent action required', value: 2},
    {label: 'Mental health', value: 3},
];

const parseCommaSeparatedEmails = (str) => str?.replace(/\s+/g, '').split(',');

const schema = yup.object().shape({
    emails: yup.string()
        .trim()
        .required(requiredError)
        .test(
            'validEmails',
            invalidEmailFormat,
            (value) => parseCommaSeparatedEmails(value)?.every(strictValidateEmail)
        ),
    template: yup.number()
        .required(requiredError),
});


const ConsultationDetails = ({ value: appointment }) => {
    const user = useChosenPatientContext();

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [gpMatch, setGpMatch] = useState(null);

    // A review-candidate match is a SUGGESTION, never a pre-fill: the admin must accept it before
    // its email reaches the recipient field. null = undecided, true = accepted, false = rejected.
    const [suggestionAccepted, setSuggestionAccepted] = useState(null);

    // A surgery the admin found by searching, used when there is no match or they rejected one.
    const [pickedSurgery, setPickedSurgery] = useState(null);
    const [surgeryQuery, setSurgeryQuery] = useState('');
    const [surgeryResults, setSurgeryResults] = useState(null);
    const [isSearching, setIsSearching] = useState(false);

    // Inline correction of a surgery's stored email (the bounce case).
    const [editingEmail, setEditingEmail] = useState(null);
    const [isSavingEmail, setIsSavingEmail] = useState(false);

    // The surgery whose email should populate the recipient field: an explicit search pick wins,
    // otherwise a confident match, otherwise an accepted suggestion. A review candidate that has
    // not been accepted deliberately contributes nothing.
    const activeSurgery = pickedSurgery
        || (gpMatch?.matched && (!gpMatch.requiresConfirmation || suggestionAccepted === true) ? gpMatch : null);

    const showSuggestion = Boolean(gpMatch?.matched && gpMatch.requiresConfirmation)
        && suggestionAccepted === null
        && !pickedSurgery;

    // Search is offered whenever nothing is currently chosen — no match, a rejected suggestion, or
    // an undecided one the admin would rather look past.
    const showSearch = !activeSurgery;

    const resetSurgeryState = () => {
        setGpMatch(null);
        setSuggestionAccepted(null);
        setPickedSurgery(null);
        setSurgeryQuery('');
        setSurgeryResults(null);
        setEditingEmail(null);
    };

    const handleSurgerySearch = async () => {
        const query = surgeryQuery.trim();
        if (!query) {
            return;
        }

        setIsSearching(true);
        try {
            const res = await PatientsManagementService.searchSurgeryContacts(query);
            setSurgeryResults(res?.data ?? []);
        } catch {
            setSurgeryResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSaveSurgeryEmail = async (surgeryId) => {
        const email = (editingEmail?.value || '').trim();
        if (!surgeryId || !strictValidateEmail(email)) {
            return toastr.warning('Enter a valid email address');
        }

        setIsSavingEmail(true);
        try {
            const res = await PatientsManagementService.updateSurgeryContactEmail(surgeryId, email);
            const updated = res?.data;

            // Reflect the correction wherever the surgery is currently shown.
            if (pickedSurgery?.id === surgeryId) {
                setPickedSurgery({ ...pickedSurgery, email });
            }
            if (gpMatch?.id === surgeryId) {
                setGpMatch({ ...gpMatch, email });
            }
            setSurgeryResults((results) => (results || []).map(
                (item) => (item.id === surgeryId ? { ...item, email } : item)
            ));

            setEditingEmail(null);
            toastr.success(`Saved — future letters to ${updated?.practiceName || 'this surgery'} will use ${email}`);
        } finally {
            setIsSavingEmail(false);
        }
    };

    const handleSendConsultation = async (data) => {
        setIsLoading(true);

        try {
            await PatientsManagementService.sendConsultationToEmails(
                appointment.consultation.id,
                {
                    ...data,
                    emails: parseCommaSeparatedEmails(data.emails),
                }
            );

            toastr.success('Consultation has been successfully sent');
            setModalIsOpen(false);
        } finally {
            setIsLoading(false);
        }
    }

    const handleClick = async () => {
        if (!user?.patient.isInfoShared) {
            return toastr.warning("The patient does not agree to share info with GP")
        }

        resetSurgeryState();
        setModalIsOpen(true)

        try {
            const res = await PatientsManagementService.getGpMatch(appointment.consultation.id);
            setGpMatch(res?.data ?? null);
        } catch {
            // A failed match is non-critical — fall back to today's manual recipient entry.
            setGpMatch(null);
        }
    }

    return (
        <div className="d-flex align-items-baseline">
            <a
                href={appointment.consultation.file.link}
                target="_blank"
                rel="noreferrer"
                className="see-details"
            >
                See details
            </a>
            <img
                src={mailWithArrow}
                alt=""
                className="ml-5 cursor-pointer"
                onClick={handleClick}
            />
            <Modal isOpen={modalIsOpen} centered>
                <Formik
                    initialValues={{
                        // Only a CHOSEN surgery fills this: a confident match, an accepted
                        // suggestion, or a search pick. An unconfirmed review candidate leaves it
                        // blank, exactly as a no-match does.
                        emails: activeSurgery?.email || '',
                        template: 0,
                    }}
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={handleSendConsultation}
                >
                    {({setFieldValue}) => (
                        <Form className="p-40px h-100 w-100">
                            <label className="d-block font-size-22 font-weight-normal">Send a discharge letter</label>
                            <Label text="Select template" htmlFor="template" className="mt-2"/>
                            <Select
                                options={TemplatesToSendGPOptions}
                                defaultValue={TemplatesToSendGPOptions[0]}
                                components={{
                                    IndicatorSeparator: () => null,
                                    Option: (props) => (
                                        <div className="search-options-big">
                                            <OptionComponent {...props}/>
                                        </div>
                                    )
                                }}
                                onChange={(option) => setFieldValue("template", option.value)}
                            />
                            <ErrorMessage name="template" render={errorMessage}/>

                            {/* A provisional match: shown as a question, never silently filled in. */}
                            {showSuggestion && (
                                <section className="mt-4 p-3 border rounded">
                                    <Label text="Possible GP surgery — please confirm"/>
                                    <p className="mb-1 font-weight-bold">
                                        {gpMatch.practiceName}{gpMatch.postcode ? ` — ${gpMatch.postcode}` : ''}
                                    </p>
                                    {gpMatch.email && (
                                        <p className="mb-1 font-size-14">{gpMatch.email}</p>
                                    )}
                                    {gpMatch.confirmationReason && (
                                        <p className="text-warning font-size-14 mb-2">{gpMatch.confirmationReason}</p>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm mr-2"
                                        onClick={() => setSuggestionAccepted(true)}
                                    >
                                        Yes, use this surgery
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setSuggestionAccepted(false)}
                                    >
                                        No, search instead
                                    </button>
                                </section>
                            )}

                            {/* The chosen surgery: a confident match, an accepted suggestion, or a search pick. */}
                            {activeSurgery && (
                                <section className="mt-4">
                                    <Label text="GP surgery"/>
                                    <p className="mb-1 font-weight-bold">
                                        {activeSurgery.practiceName}
                                        {activeSurgery.postcode ? ` — ${activeSurgery.postcode}` : ''}
                                    </p>

                                    {editingEmail?.id === activeSurgery.id ? (
                                        <div>
                                            <input
                                                type="text"
                                                className="form-control w-100"
                                                value={editingEmail.value}
                                                onChange={(e) => setEditingEmail({ id: activeSurgery.id, value: e.target.value })}
                                            />
                                            {editingEmail.value && !editingEmail.value.endsWith('@nhs.net') && (
                                                <p className="text-warning font-size-14 mb-1">
                                                    This is not an @nhs.net address — double-check it is correct for this surgery.
                                                </p>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm mr-2 mt-1"
                                                disabled={isSavingEmail}
                                                onClick={() => handleSaveSurgeryEmail(activeSurgery.id)}
                                            >
                                                Save email
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm mt-1"
                                                disabled={isSavingEmail}
                                                onClick={() => setEditingEmail(null)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            <span className="font-size-14 mr-2">
                                                {activeSurgery.email || 'No email on file — enter one below'}
                                            </span>
                                            {activeSurgery.id && (
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm p-0"
                                                    onClick={() => setEditingEmail({
                                                        id: activeSurgery.id,
                                                        value: activeSurgery.email || '',
                                                    })}
                                                >
                                                    Edit email
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 mt-1"
                                        onClick={() => { setPickedSurgery(null); setSuggestionAccepted(false); setEditingEmail(null); }}
                                    >
                                        Choose a different surgery
                                    </button>
                                </section>
                            )}

                            {/* Nothing chosen: let the admin find the surgery rather than retype an address. */}
                            {showSearch && (
                                <section className="mt-4">
                                    <Label text="Find GP surgery"/>
                                    <div className="d-flex">
                                        <input
                                            type="text"
                                            className="form-control w-100"
                                            placeholder="Search by practice name, postcode or email"
                                            value={surgeryQuery}
                                            onChange={(e) => setSurgeryQuery(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSurgerySearch(); } }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm ml-2"
                                            disabled={isSearching}
                                            onClick={handleSurgerySearch}
                                        >
                                            Search
                                        </button>
                                    </div>

                                    {surgeryResults && surgeryResults.length === 0 && !isSearching && (
                                        <p className="font-size-14 mt-2 mb-0">
                                            No surgeries found — enter the email manually below.
                                        </p>
                                    )}

                                    {surgeryResults && surgeryResults.map((item) => (
                                        <div key={item.id} className="mt-2">
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 text-left"
                                                onClick={() => { setPickedSurgery(item); setSurgeryResults(null); }}
                                            >
                                                {item.practiceName}{item.postcode ? ` — ${item.postcode}` : ''}
                                                {item.email ? ` (${item.email})` : ' (no email on file)'}
                                            </button>
                                        </div>
                                    ))}
                                </section>
                            )}

                            <section className="mt-4">
                                <Label htmlFor="emails" text="Email addresses"/>
                                <Field
                                    type="text"
                                    name="emails"
                                    className="form-control w-100"
                                    placeholder="Enter comma separated email addresses"
                                />
                                <div className="position-absolute">
                                    <ErrorMessage name="emails" className="position-relative" render={errorMessage}/>
                                </div>
                            </section>

                            <section
                                className="d-flex flex-column flex-sm-row w-100 justify-content-center modal-actions coupon-form-wrapper-buttons"
                            >
                                <button
                                    type="submit"
                                    className="btn btn-primary w-100"
                                    disabled={isLoading}
                                >
                                    Send
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger w-100"
                                    onClick={() => setModalIsOpen(false)}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                            </section>
                        </Form>
                    )}
                </Formik>
            </Modal>
        </div>
    );
};

export default ConsultationDetails;
